<template>
  <div class="capacitor-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$C = ε·S / 4πkd | Q = C·U$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
             <label>电容量 C</label>
             <span class="val">{{ capVal.toFixed(1) }} <small>pF</small></span>
          </div>
          <div class="hud-item orange">
             <label>电荷量 Q</label>
             <span class="val">{{ chargeVal.toFixed(1) }} <small>pC</small></span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 400 240" preserveAspectRatio="xMidYMid meet">
          <defs>
             <marker id="arr-e" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="#a855f7" /></marker>
          </defs>

          <!-- 电场线 (Electric Field) - 密度随 d 和 U 动态变化 -->
          <g v-if="voltage > 0">
             <line v-for="i in fieldLineCount" :key="'f'+i" 
                   :x1="plateA_X" :y1="getPlateY(i)" :x2="plateB_X" :y2="getPlateY(i)" 
                   stroke="#a855f7" :stroke-width="voltage/30" stroke-dasharray="4,2" marker-end="url(#arr-e)" opacity="0.6" />
          </g>

          <!-- 极板 A (+) -->
          <rect :x="plateA_X - 10" y="60" width="10" height="120" fill="#f43f5e" rx="2" />
          <text :x="plateA_X - 15" y="125" text-anchor="end" fill="#f43f5e" font-size="10" font-weight="900" :transform="`rotate(-90, ${plateA_X - 15}, 125)`">极板 A (+)</text>
          
          <!-- 极板 B (-) -->
          <rect :x="plateB_X" y="60" width="10" height="120" fill="#0ea5e9" rx="2" />
          <text :x="plateB_X + 25" y="125" fill="#0ea5e9" font-size="10" font-weight="900" :transform="`rotate(-90, ${plateB_X + 25}, 125)`">极板 B (-)</text>

          <!-- 动态电荷球 (Charge Distribution) -->
          <g v-for="i in fieldLineCount" :key="'c'+i">
             <circle :cx="plateA_X - 5" :cy="getPlateY(i)" r="3" fill="#fff" />
             <circle :cx="plateB_X + 5" :cy="getPlateY(i)" r="3" fill="#fff" opacity="0.8" />
          </g>
        </svg>

        <div class="field-intensity-tag">
           场强 E = {{ (voltage/distance).toFixed(1) }} V/cm
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">极板间距 (d)</label>
                 <el-slider v-model="distance" :min="10" :max="100" size="small"></el-slider>
              </div>
              <div class="flex-1">
                 <label class="s-label">充电电压 (U)</label>
                 <el-slider v-model="voltage" :min="0" :max="100" size="small"></el-slider>
              </div>
           </div>
        </div>

        <!-- 核心修改：修复文字看不清问题，采用深绿色高对比度卡片 -->
        <div class="discovery-card-discovery" v-latex>
           <div class="icon-wrap"><i class="el-icon-lighting"></i></div>
           <div class="text-content">
             <p><strong>实战物理：</strong>电容反映了电容器储存电荷的本性。<strong>观察规律：</strong>
                1. 距离 d 越小，电场线越密，电容 C 越大；
                2. 电压 U 越高，极板上的电荷点越多，说明储存的电量 Q 越多。
                这是一个由几何尺寸决定的“能量水箱”。
             </p>
           </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CapacitorViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      distance: 40,
      voltage: 60
    }
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    }
  },
  computed: {
    plateA_X() { return 200 - this.distance; },
    plateB_X() { return 200 + this.distance; },
    capVal() { return 1000 / this.distance; }, // Simplified relation
    chargeVal() { return (this.capVal * this.voltage) / 50; },
    fieldLineCount() { 
       // Number of lines depend on charge or voltage for visual richness
       return Math.floor(this.voltage / 10) + 2;
    }
  },
  methods: {
    detectMode() {
      const kp = (this.knowledgePoint || '').toString();
      if (/间距|距离/.test(kp)) this.distance = 20;
      else if (/电压|充满/.test(kp)) this.voltage = 90;
    },
    getPlateY(i) {
       return 80 + (i-1) * (100 / (this.fieldLineCount-1 || 1));
    }
  }
}
</script>

<style scoped lang="scss">
.capacitor-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0369a1; border: 1px solid #bae6fd; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; } &.cyan { border-left: 3px solid #0ea5e9; } &.orange { border-left: 3px solid #fbbf24; } } }
.field-intensity-tag { position: absolute; bottom: 8px; width: 100%; text-align: center; color: #334155; font-size: 11px; font-weight: bold; }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; }
.flex-row { display: flex; flex-direction: row; } .items-center { align-items: center; } .gap-6 { gap: 24px; } .flex-1 { flex: 1; }

/* 修复重点：高对比度发现卡片 */
.discovery-card-discovery { 
  background: #f0fdf4; // 翡翠绿底，高对比度
  padding: 12px 16px; 
  border-radius: 10px; 
  border: 1px solid #bbf7d0; 
  display: flex; gap: 14px; 
  align-items: center;
  .icon-wrap i { color: #16a34a; font-size: 1.4rem; }
  .text-content p { 
    margin: 0; 
    font-size: 12px; 
    color: #166534; // 深绿色文字，确保清晰易读
    line-height: 1.6;
    strong { color: #14532d; font-weight: 900; }
  }
}
</style>
